import { createInterface, emitKeypressEvents, type Key } from "node:readline";
import { Writable } from "node:stream";
import stringWidth from "string-width";

const silentOutput = new Writable({
  write(_chunk, _encoding, callback) {
    callback();
  },
});

export type InteractivePromptStatus = "active" | "submitted" | "cancelled";

// Interactive prompts default to the process std streams, but callers can pass
// an explicit terminal (e.g. /dev/tty) so a prompt can run even when stdin is
// redirected to /dev/null — as happens in the `curl | sh` setup bootstrap.
export type InteractivePromptStreams = {
  input: NodeJS.ReadStream;
  output: NodeJS.WriteStream;
};

export type InteractivePromptKeypressResult<State, Result> =
  | { type: "state"; state: State }
  | {
      type: "result";
      result: Result;
      status: Exclude<InteractivePromptStatus, "active">;
      state?: State;
    }
  | { type: "ignore" };

type RunInteractivePromptInput<State, Result> = {
  initialState: State;
  fallbackResult: Result;
  renderLines: (state: State, status: InteractivePromptStatus) => string[];
  handleKeypress: (input: {
    state: State;
    str: string | undefined;
    key: Key;
  }) => InteractivePromptKeypressResult<State, Result>;
};

export function runInteractivePrompt<State, Result>(
  input: RunInteractivePromptInput<State, Result>,
  streams?: InteractivePromptStreams
): Promise<Result> {
  // Keep wrap-aware prompt rendering in one place so every interactive prompt
  // clears the full terminal frame after line wrapping.
  const inputStream = streams?.input ?? process.stdin;
  const outputStream = streams?.output ?? process.stdout;
  if (
    inputStream.isTTY !== true ||
    outputStream.isTTY !== true ||
    typeof inputStream.setRawMode !== "function"
  ) {
    return Promise.resolve(input.fallbackResult);
  }

  return new Promise((resolve) => {
    const rl = createInterface({
      input: inputStream,
      output: silentOutput,
      terminal: false,
    });

    let state = input.initialState;
    let lastRenderHeight = 0;
    let cleanedUp = false;

    emitKeypressEvents(inputStream, rl);
    inputStream.setRawMode(true);
    inputStream.resume();

    const clearRender = () => {
      if (lastRenderHeight <= 0) {
        return;
      }
      outputStream.write(`\u001B[${String(lastRenderHeight)}A`);
      for (let index = 0; index < lastRenderHeight; index += 1) {
        outputStream.write("\u001B[2K\u001B[1B");
      }
      outputStream.write(`\u001B[${String(lastRenderHeight)}A`);
    };

    const render = (status: InteractivePromptStatus = "active") => {
      clearRender();
      const lines = input.renderLines(state, status);
      outputStream.write(`${lines.join("\n")}\n`);
      lastRenderHeight = measureRenderedTerminalLinesHeight(
        lines,
        outputStream.columns
      );
    };

    const cleanup = () => {
      if (cleanedUp) {
        return;
      }
      cleanedUp = true;
      inputStream.removeListener("keypress", onKeypress);
      inputStream.setRawMode(false);
      rl.close();
    };

    const finish = (
      result: Result,
      status: Exclude<InteractivePromptStatus, "active">,
      nextState?: State
    ) => {
      if (nextState) {
        state = nextState;
      }
      render(status);
      cleanup();
      resolve(result);
    };

    const onKeypress = (str: string | undefined, key: Key) => {
      const keypressResult = input.handleKeypress({
        state,
        str,
        key,
      });
      if (keypressResult.type === "ignore") {
        return;
      }
      if (keypressResult.type === "result") {
        finish(
          keypressResult.result,
          keypressResult.status,
          keypressResult.state
        );
        return;
      }
      state = keypressResult.state;
      render();
    };

    inputStream.on("keypress", onKeypress);
    render();
  });
}

export function measureRenderedTerminalLinesHeight(
  lines: string[],
  columns: number | undefined
): number {
  const normalizedColumns =
    typeof columns === "number" && Number.isFinite(columns) && columns > 0
      ? Math.trunc(columns)
      : null;
  if (normalizedColumns === null) {
    return lines.length;
  }

  return lines.reduce(
    (height, line) =>
      height + measureRenderedTerminalLineHeight(line, normalizedColumns),
    0
  );
}

export function measureRenderedTerminalLineHeight(
  line: string,
  columns: number
): number {
  const width = stringWidth(line);
  if (width <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(width / columns));
}
